import { css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { IImpersonationContextComponentProps, ImpersonationContextKey } from '../../../models';
import { CampaignType } from '../../../models/AdminModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { THEME } from '../../components-v2/Theme/theme';
import { Checkbox } from '../../components/Checkbox';
import { BlogIcon } from '../../components/svgs/icons/BlogIcon';
import { EmailIcon } from '../../components/svgs/icons/EmailIcon';
import { HeartAndShareIcon } from '../../components/svgs/icons/HeartAndShareIcon';
import { bs } from '../../styles/styles';
import { SupportedCampaignTypes } from './models';
import { styleSheet } from './styles';

function CardKeyLabel({
	type,
	active,
	hideIcons = false,
}: {
	type: CampaignType;
	active: boolean;
	hideIcons?: boolean;
}) {
	if (type === CampaignType.Email) {
		return (
			<>{!hideIcons && <EmailIcon fillColor={active ? THEME.colors.primary.main : THEME.colors.icon.gray} />} Email</>
		);
	}
	if (type === CampaignType.Social) {
		return (
			<>
				{!hideIcons && <HeartAndShareIcon fillColor={active ? THEME.colors.primary.main : THEME.colors.icon.gray} />}{' '}
				Social Media
			</>
		);
	}

	if (type === CampaignType.Blog) {
		return <>{!hideIcons && <BlogIcon fill={active ? THEME.colors.primary.main : THEME.colors.icon.gray} />} Blogs</>;
	}

	return null;
}

interface ContentCalendarLegendProps extends IImpersonationContextComponentProps {
	campaignTypes: CampaignType[];
	onSetCampaignTypes: (campaignTypes: CampaignType[]) => void;
	rightSection?: React.ReactNode;
}

export const CampaignTypeLabel = ({
	type,
	label,
	hideIcons = false,
}: {
	type: CampaignType;
	label?: string;
	hideIcons?: boolean;
}) => {
	return (
		<div className={css(styleSheet.checkboxContent)}>
			<span
				className={css(
					styleSheet.calendarCardTypeColor,
					type === CampaignType.Email ? styleSheet.calendarCardTypeColorEmail : undefined,
					type === CampaignType.Social ? styleSheet.calendarCardTypeColorSocial : undefined,
					type === CampaignType.Blog ? styleSheet.calendarCardTypeColorBlog : undefined
				)}
			/>
			<span className={css(styleSheet.showLabel)}>
				{label ? label : <CardKeyLabel type={type} active={false} hideIcons={hideIcons} />}
			</span>
		</div>
	);
};

const _ContentCalendarLegend = ({
	campaignTypes,
	onSetCampaignTypes,
	impersonationContext,
	rightSection,
}: ContentCalendarLegendProps) => {
	const userSession = useUserSession();
	const account = impersonationContext?.isValid ? impersonationContext.account : userSession?.account;
	const blogsEnabled = account?.features?.blogFeature.enabled;
	const isSocialEnabled =
		account?.features?.socialMedia.enabled &&
		userSession.user?.userGroupPermissions?.featurePermissions?.socialMedia?.send;

	const handleTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { id, checked } = e.target;
		const type = id.replace('type-', '') as CampaignType;
		if (checked) {
			onSetCampaignTypes([...campaignTypes, type]);
			return;
		}

		let nextTypes = campaignTypes.filter(t => t !== type);
		if (nextTypes.length === 0) {
			nextTypes = [CampaignType.Email];
			if (isSocialEnabled) {
				nextTypes.push(CampaignType.Social);
			}
			if (blogsEnabled) {
				nextTypes.push(CampaignType.Blog);
			}
		}
		onSetCampaignTypes(nextTypes);
	};

	return (
		<ul className={css(bs.flex, bs.horizontalStack, bs.wFull, bs.gap2, bs.pl2)}>
			{SupportedCampaignTypes.map((type, i) => {
				if ((type === CampaignType.Blog && !blogsEnabled) || (type === CampaignType.Social && !isSocialEnabled)) {
					return null;
				}

				return (
					<li className={css(bs.block, i === 0 ? styleSheet.listItem : undefined)} key={type}>
						<Checkbox id={`type-${type}`} onChange={handleTypeChange} checked={campaignTypes.includes(type)}>
							<CampaignTypeLabel type={type} hideIcons={true} />
						</Checkbox>
					</li>
				);
			})}

			{rightSection && <li className={css(bs.block, styleSheet.rightSection)}>{rightSection}</li>}
		</ul>
	);
};
const contentCalendarLegendObserver = observer(_ContentCalendarLegend);
export const ContentCalendarLegend = inject(ImpersonationContextKey)(contentCalendarLegendObserver);

interface ContentCalendarTabsProps {
	campaignTypes: CampaignType[];
	activeCampaignType: CampaignType;
	onSetCampaignActiveCampaignType: (campaignType: CampaignType) => void;
	selectedCountsByType?: Partial<Record<CampaignType, number>>;
	rightSection?: React.ReactNode;
}

export const ContentCalendarTabs = ({
	campaignTypes,
	activeCampaignType,
	onSetCampaignActiveCampaignType,
	selectedCountsByType,
	rightSection,
}: ContentCalendarTabsProps) => {
	return (
		<ul className={css(styleSheet.ContentCalendarTabs)}>
			{campaignTypes.map(type => (
				<li
					className={css(
						bs.block,
						styleSheet.calendarCardTabItem,
						type === activeCampaignType ? styleSheet.calendarCardTabItemActive : undefined
					)}
					key={type}
				>
					<button
						className={css(styleSheet.calendarCardTypeTab)}
						id={`type-${type}`}
						onClick={() => onSetCampaignActiveCampaignType(type)}
					>
						<CardKeyLabel type={type} active={type === activeCampaignType} />
						{selectedCountsByType?.[type] > 0 && (
							<span className={css(styleSheet.selectedCount)}>({selectedCountsByType[type]} selected)</span>
						)}
					</button>
				</li>
			))}

			<li className={css(styleSheet.calendarCardTabItemRightSection)}>{rightSection}</li>
		</ul>
	);
};
